import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { BackgroundOverlay } from '@/components/Tile';
import { useNavigateToAgentsEditor } from '@/hooks/navigation/useNavigateToAgentsEditor';
import Code from '@/images/Code.svg';
import DangerCircle2 from '@/images/DangerCircle2.svg';
import LoadingAnimation from '@/images/LoadingAnimation.json';
import PenOutlined from '@/images/PenOutlined.svg';
import { useCloneAgent } from '@/pages/agents/api';
import { AgentOptionsList, currentAgentOptionList as _currentAgentOptionList } from '@/pages/agents/components/AgentOptionsList';
import { UpgradeV2Button } from '@/pages/agents/components/UpgradeV2Button';
import { AGENT_DEFAULT_NAME } from '@/pages/agents/constants';
import { isKeyInUserLocalStorage } from '@/pages/agents/utils';
import { isAgentV2 } from '@/pages/avatar/hooks/useAgentIsV2';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useAgentUiFlag } from '@/services/featureFlags';
import { formatDate } from '@/utils/date';
import { AppRoutes } from '@/utils/types';
import { AgentConvertStatus, AgentStatus, IAgent } from '@d-id/types';
import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { LibraryItemHoverButton } from '../Library/LibraryItemHoverButton';
import { AgentTileInfo } from './AgentTileInfo';

export const AGENT_TILE_WIDTH = 270;

const ControlsContainer = styled.div`
    opacity: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 5;
    transition: all 0.2s ease-in-out;
`;

const ButtonsContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    padding: 1px;
    z-index: 3;

    display: flex;
    gap: 2px;
    align-items: center;
    width: 100%;
    justify-content: flex-end;
`;

const MiddleContainer = styled.div`
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    color: ${({ theme }) => theme.colors.secondary[900]};
    font-family: Mulish;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.32px;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ImageContainer = styled.div<{ hovered?: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    aspect-ratio: 16/9;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    position: relative;
    overflow: hidden;
    cursor: pointer;

    ${({ hovered }) =>
        hovered &&
        `
        .BackgroundOverlay {
            opacity: 0.5;
            transform: scale(105%);
        }
    
        ${ControlsContainer} {
            opacity: 1;
        }
    `}

    :hover .BackgroundOverlay {
        opacity: 0.5;
        transform: scale(105%);
    }

    :hover ${ControlsContainer} {
        opacity: 1;
    }
`;

const Header = styled.div`
    position: absolute;
    z-index: 2;
    top: 0;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 8px;
    > span {
        display: flex;
        padding: 2px 8px;
        align-items: flex-start;
        border-radius: 4px;
        background: #010101;
    }
`;

const Name = styled.span`
    color: white;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.24px;
`;
const Indicator = styled.div`
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 8px;
    bottom: 8px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    height: 20px;
`;

const EmbeddableIndicator = styled(Indicator)`
    color: ${({ theme }) => theme.colors.success[750]};
    border: 1px solid ${({ theme }) => theme.colors.success[550]};
    background-color: #dff7e9;
`;

const DraftIndicator = styled(Indicator)`
    color: ${({ theme }) => theme.colors.primary[80]};
    background-color: ${({ theme }) => theme.colors.black[50]};
`;

const Loading = styled(Player)<{ isSmall?: boolean }>`
    height: 64px;
    height: 64px;
`;

const Image = styled.img`
    width: 40px;
    height: 40px;
`;

interface Props {
    agent: IAgent;
    onDelete: (agent: IAgent) => void;
    onEmbed: (agent: IAgent) => void;
    onRemoveEmbed: (agent: IAgent) => void;
    onEdit: (agent: IAgent) => void;
    onUpgrade?: (agent: IAgent) => void;
    clientKey: any;
}

const AgentTile = ({ agent, onDelete, onEmbed, onRemoveEmbed, onUpgrade, clientKey, onEdit }: Props) => {
    const [keepHover, setKeepHover] = useState(false);
    const setCurrentAgentOptionList = useSetRecoilState(_currentAgentOptionList);
    const navigate = useNavigate();
    const mixpanel = useAnalytics();
    const { mutateAsync: migrate } = useCloneAgent();
    const { user } = useAuth();

    const canStartConversation = agent.status === AgentStatus.Done;
    const canEditDraft = agent.status === AgentStatus.Created;
    const isLoading = [AgentStatus.Started, AgentStatus.Ready].includes(agent?.status as AgentStatus);
    const isError = agent?.status === AgentStatus.Error;
    const isDraft = agent?.status === AgentStatus.Created;
    const isEmbed = !!agent.embed && (!agent.use_case || agent.use_case === 'd-id-agents');
    const isDisabled = isError || isLoading;
    const formattedDate = formatDate(agent.created_at);

    const { cutoff } = useAgentUiFlag();
    const hasConversion = agent.conversion === AgentConvertStatus.V2Convertable;
    const hasConverted = agent.conversion === AgentConvertStatus.V2Converted;
    const showV2Upgrade = isAgentV2(agent?.id) && !cutoff;
    const skipMigrationModal = isKeyInUserLocalStorage(user?.email, 'dontShowUpgradeV2Modal');
    const { navigateToAgentEditor } = useNavigateToAgentsEditor();

    const handleEditDraft = () => {
        mixpanel?.track('workspace-item', { event: 'click', section: 'agents', id: 'edit-draft' });

        navigateToAgentEditor({ agentId: agent.id, mixPanelFrom: 'agent-tile' });
    };

    const handleStartChat = async () => {
        if (clientKey) {
            mixpanel?.track('workspace-item', { event: 'click', section: 'agents', id: 'start-conversation' });

            navigate(`${AppRoutes.AgentShare}`, { state: { isOwner: true, agent, clientKey } });
        }
    };

    const migrateButtonClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();

        if (skipMigrationModal) {
            mixpanel?.track('agents-upgrade-v2', { event: 'click', from: 'direct' });
            await migrate(agent.id!);
        } else {
            onUpgrade?.(agent);
        }
    };

    const handleClick = () => {
        if (isDisabled) return;

        const action = canStartConversation ? handleStartChat : handleEditDraft;

        action();
    };

    return (
        <Container>
            <ImageContainer onClick={handleClick} hovered={keepHover}>
                <BackgroundOverlay
                    bg={agent.preview_thumbnail || ''}
                    bgColor="rgba(255, 255, 255, 0.5)"
                    showBlur={false}
                    isLoading={isLoading}
                    disabled={isDisabled}
                />

                {showV2Upgrade && (
                    <Header>
                        <Name>Agents 2.0</Name>
                    </Header>
                )}

                {!canEditDraft && !hasConverted && onUpgrade && !isDisabled && (
                    <UpgradeV2Button disabled={!hasConversion} onClick={migrateButtonClick} />
                )}
                <ControlsContainer>
                    {(canStartConversation || canEditDraft) && (
                        <>
                            <LibraryItemHoverButton
                                color={canStartConversation ? 'secondary' : 'outlined'}
                                onClick={handleStartChat}
                                text={canStartConversation ? 'Start conversation' : 'Edit draft'}
                                icon={!canStartConversation && <MaskedIcon src={PenOutlined} size="16px" />}
                            />
                        </>
                    )}

                    <ButtonsContainer>
                        <AgentOptionsList
                            onEdit={onEdit}
                            clientKey={clientKey}
                            onDelete={onDelete}
                            onEmbed={onEmbed}
                            onRemoveEmbed={onRemoveEmbed}
                            showMigration={!canEditDraft && !hasConverted}
                            onMigrate={agent => migrate(agent.id!)}
                            onOpen={() => {
                                setKeepHover(true);
                                setCurrentAgentOptionList({ agent });
                            }}
                            onClose={() => setKeepHover(false)}
                        />
                    </ButtonsContainer>
                </ControlsContainer>

                {isLoading && (
                    <MiddleContainer>
                        <Loading src={LoadingAnimation} autoplay loop />
                    </MiddleContainer>
                )}

                {isError && (
                    <MiddleContainer>
                        <Image src={DangerCircle2} />
                        Failed to generate agent
                    </MiddleContainer>
                )}

                {isEmbed && (
                    <EmbeddableIndicator>
                        <MaskedIcon src={Code} size="18px" />
                    </EmbeddableIndicator>
                )}
                {isDraft && <DraftIndicator>Draft</DraftIndicator>}
            </ImageContainer>

            <AgentTileInfo
                name={agent.preview_name || AGENT_DEFAULT_NAME}
                formattedDate={formattedDate}
                isError={isError}
                isDraft={isDraft}
            />
        </Container>
    );
};

const AgentTileErrorBoundary = (props: Props): ReactElement => (
    <ErrorBoundary
        fallback={<AgentTile {...props} />}
        beforeCapture={scope => scope.setTags({ component: 'AgentTile', id: props.agent.id, status: props.agent.status })}>
        <AgentTile {...props} />
    </ErrorBoundary>
);

export { AgentTileErrorBoundary as AgentTile };
